import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Grid, Button, Divider, Card, TextField, CircularProgress } from '@mui/material';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import { FETCH_CART, GET_SHOPIFY_LISTING } from '../../graphql/queries';
import { useMutation, useQuery } from '@apollo/client';
import { ShopifyImage } from '../../graphql/generated';
import { CREATE_CART } from '../../graphql/mutations';
import { toast } from 'react-toastify';
import ProductPageSkeleton from '../../Components/Skeletons/ProductPageSkeleton';
import Cookies from 'js-cookie';
import { setLocalStorage } from '../../utils/localStorage';

const ListingDetailsPage = () => {
  const { listingId } = useParams(); // Retrieve product ID from URL
  const location = useLocation();
  const navigate = useNavigate();
  const { shop_domain } = location.state || {}; // Extract shop_domain from state
  const fakeData = {
    getShopifyListing: {
      id: "gid://shopify/Product/123",
      title: "Blue T-Shirt",
      product_type: "Tops",
      vendor: "BrandA",
      shop_domain: "store.myshopify.com",
      images: [
        { src: "https://via.placeholder.com/200x200.png?text=Blue+T-Shirt", altText: "Blue T-Shirt" },
        { src: "https://via.placeholder.com/200x200.png?text=Alt+View", altText: "Alt View" }
      ],
      options: [
        { id: "opt1", name: "Size", values: ["S", "M", "L"] },
        { id: "opt2", name: "Color", values: ["Blue", "Red"] }
      ],
      variants: [
        {
          id: "gid://shopify/ProductVariant/111",
          price: 25,
          inventoryQuantity: 5,
          selectedOptions: [
            { name: "Size", value: "S" },
            { name: "Color", value: "Blue" }
          ]
        }
      ]
    }
  }

  const { loading, error, data = fakeData } = useQuery(GET_SHOPIFY_LISTING, {
    variables: { shop_domain, id: listingId },
    skip: !shop_domain, // Skip the query if shop_domain is not available
  });

  const [selectedQuantity, setSelectedQuantity] = useState(1);

  const handleQuantityChange = (event: any) => {
    const value = Number(event.target.value);
    if (value >= 1) setSelectedQuantity(value);
  };

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  const [disabledOptions, setDisabledOptions] = useState<Record<string, Set<string>>>({});
  const [addCartLoading, setLoading] = useState<boolean>(false);
  const [checkoutLoading, setCheckoutLoading] = useState<boolean>(false);

  const availableQuantity = selectedVariant?.inventoryQuantity ?? 0;
  const isQuantityExceeded = selectedQuantity > availableQuantity;


  const [createCart] = useMutation(CREATE_CART);

  const handleAddToCart = async (e: any, load: boolean = true) => {
    e.preventDefault();
    setLoading(load);
    try {
      // Backend createCart handles both creating new carts AND adding to existing ones
      const response = await createCart({
        variables: {
          items: [
            {
              merchandiseId: `${selectedVariant.id}`,
              quantity: selectedQuantity,
            },
          ],
        },
        refetchQueries: [FETCH_CART]
      });

      const cart = response.data.createCart;

      // Always update localStorage with the latest cart ID (in case backend created a new one)
      setLocalStorage('shopifyCartId', cart.id);

      toast.success('Item added to cart!');
    } catch (error: any) {
      console.error('Error adding to cart:', error.message);
      toast.error('Failed to add item to cart. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (e: any) => {
    e.preventDefault();
    setCheckoutLoading(true);
    await handleAddToCart(e, false);
    setCheckoutLoading(false);
    // Preserve Shopify URL parameters when navigating
    navigate(`/cart${window.location.search}`)
  }


  // Update selectedOptions and selectedVariant when the product loads
  useEffect(() => {
    if (data?.getShopifyListing) {
      const product = data.getShopifyListing;

      // Initialize selectedOptions to the first value of each option
      const initialSelectedOptions: Record<string, string> = {};
      product.options.forEach((option: any) => {
        if (option.values.length > 0) {
          initialSelectedOptions[option.name] = option.values[0];
        }
      });

      setSelectedOptions(initialSelectedOptions);
      updateSelectedVariant(product.variants, initialSelectedOptions);

      // Calculate disabled options based on inventory on initial load
      updateDisabledOptions(
        product.variants,
        product.options,
        initialSelectedOptions
      );
    }
  }, [data]);

  // Save shop_domain to cookie for cart operations
  useEffect(() => {
    if (shop_domain) {
      const normalizedShop = shop_domain.includes('.myshopify.com')
        ? shop_domain
        : `${shop_domain}.myshopify.com`;
      Cookies.set("shopDomain", normalizedShop);
      localStorage.setItem("shopDomain", normalizedShop);
      console.log("💾 Saved listing shop_domain:", normalizedShop);
    }
  }, [shop_domain]);

  // Also save shop_domain from loaded data (in case location.state was lost)
  // Skip if it's the fakeData placeholder
  useEffect(() => {
    const shopFromData = data?.getShopifyListing?.shop_domain;
    if (shopFromData && shopFromData !== "store.myshopify.com") {
      const normalizedShop = shopFromData.includes('.myshopify.com')
        ? shopFromData
        : `${shopFromData}.myshopify.com`;
      Cookies.set("shopDomain", normalizedShop);
      localStorage.setItem("shopDomain", normalizedShop);
      console.log("💾 Saved shop_domain from listing data:", normalizedShop);
    }
  }, [data]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [listingId]);

  // Function to update the selectedVariant based on selectedOptions
  const updateSelectedVariant = (variants: any[], selectedOptions: Record<string, string>) => {
    const matchedVariant = variants.find((variant: any) =>
      variant.selectedOptions.every(
        (option: any) => selectedOptions[option.name] === option.value
      )
    );
    setSelectedVariant(matchedVariant || null);
    setSelectedQuantity(1); // Reset quantity when variant changes
  };

  // Function to update disabled options based on inventory and selectedOptions
  const updateDisabledOptions = (
    variants: any[],
    options: any[],
    currentSelectedOptions?: Record<string, string>
  ) => {
    const newDisabledOptions: Record<string, Set<string>> = {};
    // Use passed-in selected options or fall back to state
    const activeSelectedOptions = currentSelectedOptions || selectedOptions;

    options.forEach((option: any) => {
      const currentOptionName = option.name;
      const disabledValues = new Set<string>();

      // Iterate over the values in this option
      option.values.forEach((value: string) => {
        // Check if this value would match any variant with inventory > 0
        const isValueAvailable = variants.some((variant: any) => {
          return (
            variant.inventoryQuantity > 0 &&
            variant.selectedOptions.every((variantOption: any) => {
              // Match selected options or this option value
              if (variantOption.name === currentOptionName) {
                return variantOption.value === value;
              }
              return activeSelectedOptions[variantOption.name] === variantOption.value;
            })
          );
        });

        if (!isValueAvailable) {
          disabledValues.add(value);
        }
      });

      newDisabledOptions[currentOptionName] = disabledValues;
    });

    setDisabledOptions(newDisabledOptions);
  };

  // Handle option selection
  const handleOptionSelect = (optionName: string, value: string) => {
    const updatedOptions = { ...selectedOptions, [optionName]: value };
    setSelectedOptions(updatedOptions);

    // Update selectedVariant based on the new options
    if (data?.getShopifyListing?.variants) {
      updateSelectedVariant(data.getShopifyListing.variants, updatedOptions);
      updateDisabledOptions(
        data.getShopifyListing.variants,
        data.getShopifyListing.options,
        updatedOptions
      );
    }
  };

  if (loading) {
    return <ProductPageSkeleton />;
  }

  if (error) {
    return <Typography>Error: {error.message}</Typography>;
  }

  const product = data.getShopifyListing;

  if (!product) {
    return <Typography>No product details available.</Typography>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        padding: 3,
        backgroundColor: '#fff',
        margin: '20px auto',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
      }}
    >
      {/* Product Images Section */}
      <Grid container spacing={2}>
        {/* Image Stack */}
        <Grid item xs={2}>
          {product.images.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              border: '1px solid #ddd',
              borderRadius: 2,
              width: 124,
              overflow: 'hidden',
              justifyContent: 'center',
            }}
          >
            {product.images.map((image: ShopifyImage, index: number) => (
              <Box
                key={index}
                sx={{
                  cursor: 'pointer',
                  border: selectedImage === image.src ? '2px solid #1976d2' : 'none',
                  borderRadius: '4px',
                  width: 120,
                  height: 180,
                  justifyContent: 'center',
                  alignContent: 'center',
                  overflow: 'hidden',
                }}
                onClick={() => setSelectedImage(image.src)}
              >
                <img
                  src={image.src}
                  alt={image.altText || 'Product Thumbnail'}
                  width="100%"
                  style={{ borderRadius: '4px' }}
                />
              </Box>
            ))}
          </Box>
          )}
        </Grid>

        {/* Large Displayed Image */}
        <Grid item xs={5}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              borderRadius: '4px',
              border: '1px solid #ddd',
            }}
          >
            {selectedImage || product.images[0]?.src ? (
              <img
                src={selectedImage || product.images[0]?.src}
                alt="Selected Product"
                style={{ maxHeight: '400px', maxWidth: '100%', borderRadius: '4px' }}
              />
            ) : (
              <Box
                role="img"
                aria-label={`${product.title} — no image available`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 400,
                  width: '100%',
                  color: '#bdbdbd',
                }}
              >
                <ImageNotSupportedOutlinedIcon sx={{ fontSize: 96 }} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  No image available
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Product Details Card */}
        <Grid item xs={5}>
          <Card sx={{ padding: 3 }}>
            {/* Category Information */}
            <Typography variant="body2" color="textSecondary">
              {product.product_type || 'Category'} / {product.vendor || 'Brand'}
            </Typography>

            {/* Product Title */}
            <Typography variant="h5" sx={{ marginTop: 1 }}>
              {product.title}
            </Typography>

            {/* Price */}
            <Typography variant="h6" sx={{ marginTop: 1, marginBottom: 2 }}>
              ${selectedVariant ? selectedVariant.price : 'N/A'}
            </Typography>

            <Divider />

            {/* Product Options - Hide if only "Default Title" */}
            {product.options
              .filter((option: any) => !(option.name === "Title" && option.values.length === 1 && option.values[0] === "Default Title"))
              .map((option: any) => (
              <Box key={option.id} sx={{ marginTop: 2 }}>
                <Typography variant="body1" sx={{ marginBottom: 1 }}>
                  {option.name}:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {option.values.map((value: string, valueIndex: number) => (
                    <Button
                      key={`${option.id}-${valueIndex}`}
                      variant={selectedOptions[option.name] === value ? 'contained' : 'outlined'}
                      disabled={
                        disabledOptions[option.name]?.has(value) // Disable if unavailable
                      }
                      onClick={() => handleOptionSelect(option.name, value)}
                      sx={{ textTransform: 'none' }}
                    >
                      {value}
                    </Button>
                  ))}
                </Box>
              </Box>
            ))}

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                marginTop: 3,
                flexDirection: "column",
              }}
            >

              {selectedVariant?.inventoryQuantity != null && selectedVariant.inventoryQuantity > 0 && (
                <Typography variant="body1" sx={{ marginBottom: 1 }} alignSelf={"start"} textAlign={"start"}>
                  {selectedVariant.inventoryQuantity} available
                </Typography>
              )}
              {/* Quantity Selector */}
              <TextField
                label="Quantity"
                type="number"
                variant="outlined"
                size="small"
                value={selectedQuantity}
                onChange={handleQuantityChange}
                error={isQuantityExceeded}
                helperText={isQuantityExceeded ? `Only ${availableQuantity} available` : ''}
                inputProps={{ min: 1 }}
                sx={{ width: "100px" }}
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: "row",
                }}>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!selectedVariant || addCartLoading || !availableQuantity || isQuantityExceeded}
                  onClick={handleAddToCart}
                  sx={{ mr: 1 }}
                >
                  {
                    addCartLoading && (
                      <CircularProgress size={24} sx={{ color: "white", mr: 2 }} />
                    )
                  }
                  <span>Add to Bag</span>
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled={!selectedVariant || checkoutLoading || !availableQuantity || isQuantityExceeded}
                  onClick={handleCheckout}
                >
                  {
                    checkoutLoading && (
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                    )
                  }
                  <span>Checkout</span>
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ListingDetailsPage;
